import React, { useState, FormEvent } from 'react';
import { Calendar, User, Mail, Phone, MapPin, FileText, Info, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FormData {
  fullName: string;
  email: string;
  mobile: string;
  dob: string;
  gender: string;
  location: string;
  areas: string[];
  availability: string;
  source: string;
  bio: string;
  idProof?: File | null;
}

const VolunteerRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    mobile: '',
    dob: '',
    gender: '',
    location: '',
    areas: [],
    availability: '',
    source: '',
    bio: '',
    idProof: null
  });

  const [showThankYou, setShowThankYou] = useState(false);

  const generateVolunteerId = () => {
    const timestamp = Date.now().toString();
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `ADF-NGO-${timestamp.slice(-6)}${randomNum}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (area: string) => {
    setFormData(prev => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter(a => a !== area)
        : [...prev.areas, area]
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, idProof: file }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const volunteerId = generateVolunteerId();
    let whatsappMessage = `New Volunteer Registration:
ID: ${volunteerId}
Name: ${formData.fullName}
Email: ${formData.email}
Mobile: ${formData.mobile}
Date of Birth: ${formData.dob}
Gender: ${formData.gender}
Location: ${formData.location}
Volunteering Areas: ${formData.areas.join(', ')}
Availability: ${formData.availability}
Source: ${formData.source}
Bio: ${formData.bio}`;

    // If there's a file, add a note about it
    if (formData.idProof) {
      whatsappMessage += '\n\nNote: ID Proof document will be shared separately.';
    }

    // Create WhatsApp URL with pre-filled message
    const whatsappUrl = `https://wa.me/919698862115?text=${encodeURIComponent(whatsappMessage)}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
    
    // Show thank you message
    setShowThankYou(true);
    
    // Reset form and redirect after 3 seconds
    setTimeout(() => {
      setFormData({
        fullName: '',
        email: '',
        mobile: '',
        dob: '',
        gender: '',
        location: '',
        areas: [],
        availability: '',
        source: '',
        bio: '',
        idProof: null
      });
      setShowThankYou(false);
      navigate('/volunteers');
    }, 3000);
  };

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Volunteer Registration</h2>
            <p className="text-lg text-muted-foreground">
              Join our team of dedicated volunteers and make a difference in the community.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 bg-card p-6 rounded-lg shadow-subtle dark:shadow-subtle-dark border border-border">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium mb-2">
                  Mobile Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    required
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="dob" className="block text-sm font-medium mb-2">
                  Date of Birth *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    required
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium mb-2">
                  Gender *
                </label>
                <select
                  id="gender"
                  name="gender"
                  required
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium mb-2">
                  City / Location *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Preferred Volunteering Area *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['Teaching', 'Social Media', 'Event Support', 'Fundraising', 'Admin', 'Other'].map((area) => (
                  <label key={area} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.areas.includes(area)}
                      onChange={() => handleCheckboxChange(area)}
                      className="rounded border-input focus:ring-2 focus:ring-primary"
                    />
                    <span>{area}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="availability" className="block text-sm font-medium mb-2">
                  Availability *
                </label>
                <select
                  id="availability"
                  name="availability"
                  required
                  value={formData.availability}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">Select Availability</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>

              <div>
                <label htmlFor="source" className="block text-sm font-medium mb-2">
                  How did you hear about us? *
                </label>
                <select
                  id="source"
                  name="source"
                  required
                  value={formData.source}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">Select Source</option>
                  <option value="Website">Website</option>
                  <option value="Friend">Friend</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Event">Event</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium mb-2">
                Short Bio / Why you want to volunteer? *
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                <textarea
                  id="bio"
                  name="bio"
                  required
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                ></textarea>
              </div>
            </div>

            <div>
              <label htmlFor="idProof" className="block text-sm font-medium mb-2">
                Upload ID Proof (Optional)
              </label>
              <p className="text-sm text-muted-foreground mb-2">
                Note: Due to WhatsApp limitations, please send ID proof separately after registration.
              </p>
              <input
                type="file"
                id="idProof"
                name="idProof"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Submit Registration
            </button>
          </form>
        </div>
      </div>

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-card p-6 rounded-lg shadow-lg max-w-sm mx-4 relative animated-section">
            <button
              onClick={() => setShowThankYou(false)}
              className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/10">
                <Info className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
              <p className="text-muted-foreground">
                Your registration has been submitted successfully. We'll contact you soon!
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VolunteerRegistration;